import { useCallback, useEffect, useState } from "react";
import PostCard from "@/components/PostCard";
import EmotionFilter from "@/components/EmotionFilter";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const DEFAULT_FILTERS = { emotion: null, minIntensity: 0, minConsistency: 0, q: "" };

export default function Feed() {
  const { user, refreshUser } = useAuth();
  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        min_intensity: filters.minIntensity,
        min_consistency: filters.minConsistency,
      };
      if (filters.emotion) query.emotion = filters.emotion;
      if (filters.q) query.q = filters.q;
      const { data } = await api.get("/posts", { params: query });
      setPosts(data);
    } finally {
      setLoading(false);
    }
  }, [filters.emotion, filters.minIntensity, filters.minConsistency, filters.q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const pt = params.get("pledge_topup");
    const sid = params.get("session_id");
    if (pt === "success" && sid) {
      // Nudge the server to finalize if the webhook hasn't landed yet.
      api.get(`/payments/status/${sid}`).catch(() => {});
      toast.success("Top-up received · pledge executed from wallet");
      refreshUser?.();
      load();
    } else if (pt === "cancel") {
      toast.message("Top-up cancelled — pledge not placed");
    }
  }, [params, load, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start space-y-4">
          <div>
            <p className="telesis-eyebrow">Feed · Mentation stream</p>
            <h1 className="text-2xl md:text-3xl font-bold mt-1 text-slate-100">The ledger of feeling</h1>
            {!user?.is_member && (
              <p className="text-xs text-slate-400 mt-2">
                You can read freely. Sign in &amp; subscribe to vote and finance.
              </p>
            )}
          </div>
          <EmotionFilter filters={filters} setFilters={setFilters} onReset={() => setFilters(DEFAULT_FILTERS)} />
          <Link
            to="/create"
            className="block text-center rounded bg-yellow-400 text-black hover:bg-yellow-300 py-3 text-sm font-semibold"
          >
            + New mentation
          </Link>
        </aside>
        <section className="lg:col-span-8 space-y-4">
          {loading && <p className="text-slate-500 font-mono text-sm">loading…</p>}
          {!loading && posts.length === 0 && (
            <div className="telesis-panel rounded p-8 text-center">
              <p className="telesis-eyebrow">Empty feed</p>
              <p className="text-slate-300 mt-2 text-sm">
                No mentation yet.{" "}
                <Link to="/create" className="text-yellow-400 underline">Post the first one.</Link>
              </p>
            </div>
          )}
          {posts.map((p) => (
            <PostCard key={p.post_id} post={p} onUpdate={load} />
          ))}
        </section>
      </main>
    </div>
  );
}
